import AuthenticatorAppService from "../../../../application/platform/security/authenticator-app-service";
import { ACTION_TYPES, useClientsManagementDispatch, useClientsManagementState } from "./clients-management-context";
import { IListClientsRequest } from "../../../../model/base/request/list-clients-request";
import { ICreateClientRequest } from "../../../../model/base/request/create-client-request";
import { IUpdateClientRequest } from "../../../../model/base/request/update-client-request";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { DefaultDialogProps } from "../../../components/default-dialog";
import ClientAppSerivce from "../../../../application/platform/official-daily/client-app-service";

export const useClientsManagementController = () => {
    const { clientsPagedRequest, clientForm, isEditing, brandLogo } = useClientsManagementState();
    const clientsManagementDispatch = useClientsManagementDispatch();
    const { showBackDrop, showMessage, showDialog } = useBaseLayoutController();


    const loadData = async (request: IListClientsRequest) => {
        if (request?.page !== undefined && request?.pageSize !== undefined) {
            clientsManagementDispatch({
                type: ACTION_TYPES.setIsLoadingData,
                payload: true,
            });

            try {
                const data = await ClientAppSerivce.listClients(request);

                clientsManagementDispatch({
                    type: ACTION_TYPES.setClientsPagedResponse,
                    payload: data,
                });
            } catch (error) {
                console.error('Error loading clients:', error);
            } finally {
                clientsManagementDispatch({
                    type: ACTION_TYPES.setIsLoadingData,
                    payload: false,
                });
            }
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        // Proteção: evita re-disparo se valores não mudaram
        if (clientsPagedRequest?.page === page &&
            clientsPagedRequest?.pageSize === pageSize) {
            return;
        }

        var request = { ...clientsPagedRequest, page, pageSize } as IListClientsRequest
        clientsManagementDispatch({
            type: ACTION_TYPES.setClientsPagedRequest,
            payload: request,
        });

        await loadData(request);
    };

    const handleFilterChange = (e: any) => {
        let name = clientsPagedRequest?.name;
        let document = clientsPagedRequest?.document;
        let entityType = clientsPagedRequest?.entityType;

        if (e.target.name === "input-name") {
            name = e.target.value;
        }
        if (e.target.name === "input-document") {
            document = e.target.value;
        }
        if (e.target.name === "select-entity-type") {
            entityType = e.target.value ? parseInt(e.target.value) : undefined;
        }

        clientsManagementDispatch({
            type: ACTION_TYPES.setClientsPagedRequest,
            payload: { ...clientsPagedRequest, name, document, entityType },
        });
    };

    const clearFilters = async () => {
        const request = {
            page: 0,
            pageSize: 50,
            name: "",
            document: "",
            entityType: undefined
        } as IListClientsRequest;

        clientsManagementDispatch({
            type: ACTION_TYPES.setClientsPagedRequest,
            payload: request,
        });

        await loadData(request);
    };

    const searchClients = async () => {
        const request = { ...clientsPagedRequest, page: 0 };
        clientsManagementDispatch({
            type: ACTION_TYPES.setClientsPagedRequest,
            payload: request,
        });
        await loadData(request);
    };

    const loadClientById = async (id: number) => {
        showBackDrop(true);
        clientsManagementDispatch({
            type: ACTION_TYPES.setIsLoadingClient,
            payload: true,
        });

        try {
            const client = await ClientAppSerivce.getClientById(id);
            console.log('Loaded client:', client);
            clientsManagementDispatch({
                type: ACTION_TYPES.setSelectedClient,
                payload: client,
            });

            const formData = {
                id: client.id,
                name: client.name,
                corporateName: client.corporateName,
                document: client.document,
                brandLogo: client.brandLogo,
                entityType: client.entityType,
                webPage: client.webPage,
                instagramProfile: client.instagramProfile,
                facebookProfile: client.facebookProfile,
                twitterProfile: client.twitterProfile,
                youtubeProfile: client.youtubeProfile,
                urlServidores: client.urlServidores,
                urlTransparencia: client.urlTransparencia,
                urlOuvidoria: client.urlOuvidoria,
                urlLicitacoes: client.urlLicitacoes,
                urlContratos: client.urlContratos,
                urlNotaFiscal: client.urlNotaFiscal,
                urlSic: client.urlSic,
                urlDiarioOficial: client.urlDiarioOficial,
                address: client.address ? {
                    street: client.address.street,
                    number: client.address.number,
                    complement: client.address.complement,
                    neighborhood: client.address.neighborhood,
                    postalCode: client.address.zipCode,
                    stateId: client.address.stateId,
                    cityId: client.address.cityId
                } : undefined,
                contact: client.contact ? {
                    email: client.contact.email,
                    phone: client.contact.mobilePhone,
                    website: ""
                } : undefined
            } as ICreateClientRequest;

            clientsManagementDispatch({
                type: ACTION_TYPES.setClientForm,
                payload: formData,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar cliente.' });
        } finally {
            showBackDrop(false);

            clientsManagementDispatch({
                type: ACTION_TYPES.setIsLoadingClient,
                payload: false,
            });
        }
    };

    const handleFormChange = (field: string, value: any) => {
        clientsManagementDispatch({
            type: ACTION_TYPES.setClientForm,
            payload: { ...clientForm, [field]: value },
        });
    };

    const handleAddressChange = (field: string, value: any) => {
        const currentAddress = clientForm?.address || { cityId: 0 };
        const updatedForm = {
            ...clientForm,
            address: { ...currentAddress, [field]: value }
        };

        clientsManagementDispatch({
            type: ACTION_TYPES.setClientForm,
            payload: updatedForm,
        });
    };

    const updateAddress = (addressUpdates: any) => {
        const currentAddress = clientForm?.address || { cityId: 0 };
        const updatedForm = {
            ...clientForm,
            address: { ...currentAddress, ...addressUpdates }
        };

        clientsManagementDispatch({
            type: ACTION_TYPES.setClientForm,
            payload: updatedForm,
        });
    };

    const handleContactChange = (field: string, value: any) => {
        clientsManagementDispatch({
            type: ACTION_TYPES.setClientForm,
            payload: {
                ...clientForm,
                contact: { ...clientForm.contact, [field]: value }
            },
        });
    };

    const openDrawer = (editMode = false, clientId?: number) => {
        clientsManagementDispatch({
            type: ACTION_TYPES.setIsEditing,
            payload: editMode,
        });

        if (editMode && clientId) {
            loadClientById(clientId);
        } else {
            clientsManagementDispatch({
                type: ACTION_TYPES.resetClientForm,
                payload: null,
            });
        }

        clientsManagementDispatch({
            type: ACTION_TYPES.setIsDrawerOpen,
            payload: true,
        });
    };

    const closeDrawer = () => {
        clientsManagementDispatch({
            type: ACTION_TYPES.setIsDrawerOpen,
            payload: false,
        });

        clientsManagementDispatch({
            type: ACTION_TYPES.setSelectedClient,
            payload: null,
        });

        clientsManagementDispatch({
            type: ACTION_TYPES.setBrandLogo,
            payload: null,
        });

        clientsManagementDispatch({
            type: ACTION_TYPES.resetClientForm,
            payload: null,
        });
    };

    const saveClient = async () => {
        try {

            showBackDrop(true);
            console.log('clientForm:', clientForm);
            if (isEditing && clientForm.id) {
                const updateRequest = { ...clientForm, id: clientForm.id } as IUpdateClientRequest;
                await ClientAppSerivce.updateClient(updateRequest, brandLogo);
            } else {
                await ClientAppSerivce.createClient(clientForm, brandLogo);
            }

            showMessage({ type: 'success', message: 'Cliente salvo com sucesso.' });

            closeDrawer();

            await loadData(clientsPagedRequest);
            showBackDrop(false);

        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao salvar cliente.' });
        }
    };

    const loadStates = async () => {
        try {
            const result = await AuthenticatorAppService.listStates();
            clientsManagementDispatch({
                type: ACTION_TYPES.setListStateResponse,
                payload: result,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar estados.' });
        }
    };

    const loadCities = async (stateId: number) => {
        try {
            const result = await AuthenticatorAppService.listCities({ stateId });
            clientsManagementDispatch({
                type: ACTION_TYPES.setListCityResponse,
                payload: result,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar cidades.' });

        }
    };

    const toggleClientStatus = async (clientId: number, currentStatus: boolean) => {
        showDialog({
            open: true,
            message: `Deseja realmente ${currentStatus ? 'desativar' : 'ativar'} este cliente?`,
            onYesClick: async () => {
                try {
                    showBackDrop(true);
                    await ClientAppSerivce.enableDisableClient(clientId, !currentStatus);
                    showMessage({ type: 'success', message: 'Status atualizado com sucesso.' });
                    await loadData(clientsPagedRequest);
                } catch (error) {
                    showMessage({ type: 'error', message: 'Erro ao alterar status do cliente.' });
                } finally {
                    showBackDrop(false);
                }
            }
        } as unknown as DefaultDialogProps);
    };

    return {
        loadData,
        handleFilterChange,
        onChangePagination,
        clearFilters,
        searchClients,
        loadClientById,
        handleFormChange,
        handleAddressChange,
        handleContactChange,
        openDrawer,
        closeDrawer,
        saveClient,
        toggleClientStatus,
        loadStates,
        loadCities,
        updateAddress,
        setBrandLogo: (file: File | null) => {
            clientsManagementDispatch({
                type: ACTION_TYPES.setBrandLogo,
                payload: file,
            });
        }
    };
};